import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type {
  EffectivePlanView,
  ZuubPatientDetailDataV4,
} from '../../@types/zuub/contracts';
import type { Appointment } from '../../react-query/useAppointments';

// --- Filters ---
export const insuranceStatusFilterAtom = atom<string>('all');
/** Card filter: all | active | noCoverage | confirmDetails | checkManually */
export const selectedCardAtom = atom<
  'all' | 'active' | 'noCoverage' | 'confirmDetails' | 'checkManually'
>('active');
export const selectedPayerFiltersAtom = atom<string[]>([]);
export const selectedInsuranceStatusFiltersAtom = atom<string[]>([]);

// --- Benefits page: selected plan + network ---
export const selectedPlanRawAtom = atom<ZuubPatientDetailDataV4 | null>(null);
export const selectedNetworkIndexAtom = atom<number>(0);
const benefitsNetworkSelectionsStorage = createJSONStorage<
  Record<string, string>
>(() => localStorage);
export const benefitsNetworkSelectionsByPayerAtom = atomWithStorage<
  Record<string, string>
>(
  'autoverify.benefits.networkSelectionsByPayer',
  {},
  benefitsNetworkSelectionsStorage,
);


/** Plan + selected network merged for sections; network-scoped fields fall back to plan level. */
export const selectedPlanAtom = atom<EffectivePlanView | null>((get) => {
  const plan = get(selectedPlanRawAtom);
  const networkIndex = get(selectedNetworkIndexAtom);
  if (!plan) return null;

  const { networks, ...planWithoutNetworks } = plan;
  const planView = planWithoutNetworks as EffectivePlanView;
  if (!networks?.length) return planView;

  const network = networks[networkIndex] ?? networks[0];
  return {
    ...planView,
    coverage: network.coverage ?? planView.coverage,
    coverageTiers: network.coverageTiers ?? planView.coverageTiers,
    benefitSummary: network.benefitSummary ?? planView.benefitSummary,
    orthoBenefitSummary:
      network.orthoBenefitSummary ?? planView.orthoBenefitSummary,
    disclaimersAndLimitations:
      network.disclaimersAndLimitations ?? planView.disclaimersAndLimitations,
    maximumsAndDeductibles:
      network.maximumsAndDeductibles ?? planView.maximumsAndDeductibles,
    waitingPeriods: network.waitingPeriods ?? planView.waitingPeriods,
  } as EffectivePlanView;
});

// --- Other selection ---
export const selectedPatientAppointmentAtom = atom<Appointment | null>(null);
export const selectedPayerIdAtom = atom<number | null>(null);
